import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { Download, Loader2 } from "lucide-react";
import { CONTACT } from "@/data/products";
import { scrollToSection } from "@/lib/smooth";
import { WaIcon } from "@/components/site/Primitives";

pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";

// Replace this file to update the catalogue; the page address stays /catalogue
const PDF_URL = "/files/praveen-textiles-catalogue.pdf";
const WA_URL = `https://wa.me/919889886321?text=${encodeURIComponent("Hi Praveen Textiles, I've seen your catalogue and would like current stock and rates for: ")}`;

const useWidth = (ref) => {
  const [w, setW] = useState(0);
  useEffect(() => {
    const ro = new ResizeObserver(([e]) => setW(Math.floor(e.contentRect.width)));
    if (ref.current) ro.observe(ref.current);
    return () => ro.disconnect();
  }, [ref]);
  return w;
};

const Actions = ({ suffix = "" }) => (
  <div className="flex flex-wrap gap-3">
    <a data-testid={`catalogue-download-btn${suffix}`} href={PDF_URL} download="Praveen-Textiles-Catalogue.pdf" className="flex items-center gap-2 rounded-full bg-crimson px-6 py-3.5 font-body text-sm font-semibold text-parchment transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-crimson-bright">
      <Download size={16} /> Download Catalogue
    </a>
    <a data-testid={`catalogue-whatsapp-btn${suffix}`} href={WA_URL} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 rounded-full bg-[#1FA855] px-6 py-3.5 font-body text-sm font-semibold text-white transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-[#25D366]">
      <WaIcon className="h-[18px] w-[18px]" /> Chat on WhatsApp
    </a>
  </div>
);

export default function Catalogue() {
  const box = useRef(null);
  const width = useWidth(box);
  const [pages, setPages] = useState(0);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    scrollToSection(0);
  }, []);

  return (
    <div data-testid="catalogue-page" className="min-h-screen bg-parchment">
      <header className="flex items-center justify-between px-6 py-5 sm:px-12 lg:px-20">
        <Link to="/" data-testid="catalogue-logo-link" aria-label="Praveen Textiles — home">
          <img src="/logo-full.png" alt="Praveen Textiles" className="h-12 w-auto sm:h-14" />
        </Link>
      </header>
      <main className="px-4 pb-28 pt-6 sm:px-12 lg:px-20">
        <div className="mx-auto max-w-4xl">
          <p className="flex items-center gap-4 font-accent text-[11px] font-semibold tracking-[0.35em] text-crimson">
            <span className="h-px w-12 bg-crimson" /> CATALOGUE
          </p>
          <h1 className="mt-5 font-display text-4xl font-light tracking-tight sm:text-5xl">Praveen Textiles Catalogue</h1>
          <p className="mt-3 font-body text-sm text-clay">Our full range in one place. Message us on WhatsApp for current stock and rates.</p>
          <div className="mt-8"><Actions /></div>

          <div ref={box} data-testid="catalogue-viewer" className="mt-10 space-y-4">
            {failed ? (
              <p data-testid="catalogue-error" className="border border-dashed border-ink/20 p-10 text-center text-sm text-clay">
                The catalogue preview could not load. Please use Download Catalogue above.
              </p>
            ) : (
              <Document
                file={PDF_URL}
                onLoadSuccess={({ numPages }) => setPages(numPages)}
                onLoadError={() => setFailed(true)}
                loading={<div className="flex items-center justify-center gap-2 py-20 text-sm text-clay"><Loader2 size={16} className="animate-spin" /> Loading catalogue…</div>}
              >
                {width > 0 && Array.from({ length: pages }, (_, i) => (
                  <div key={i} data-testid={`catalogue-page-${i + 1}`} className="mb-4 overflow-hidden border border-ink/10 bg-white shadow-[0_20px_50px_-30px_rgba(24,24,26,0.35)]">
                    <Page pageNumber={i + 1} width={width} renderAnnotationLayer={false} renderTextLayer={false} />
                  </div>
                ))}
              </Document>
            )}
          </div>
          {pages > 1 && <div className="mt-8"><Actions suffix="-bottom" /></div>}
        </div>
      </main>
    </div>
  );
}
